import Grid from '@mui/material/Grid';
import type {
  AppInventoryOptionType,
  AppInventoryState,
  AppInventoryVersionType,
} from '@openx/types/targeting/appInventory';
import {
  AppInventoryType,
  CustomBlockingRulesOptions,
  type InventoryAndContentState,
  InventoryContentOption,
  type PublisherInventoryRuleOptions,
} from '@openx/types/targeting/inventoryAndContent';
import type { DisplayContextsType } from '@openx/types/targeting/targeting';
import type { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import { removeWhiteSpaces } from '@openx/utils/lib/removeWhiteSpaces';
import { type Dispatch, type JSX, type SetStateAction, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BulkAddDrawer } from '../../../shared/BulkAdd/BulkAddDrawer';
import type { InvalidItemsError, ValidationResult, ValidationResultV2 } from '../../../shared/BulkAdd/types';
import { InventoryComparisonTypeDropdown } from '../../../shared/InventoryAndContent/components/InventoryComparisonTypeDropdown';
import { InventoryContentOptionDropdown } from '../../../shared/InventoryAndContent/components/InventoryContentOptionDropdown';
import {
  bulkAddHelperPlaceholders,
  INVENTORY_LIMITS,
  useInventoryAndContentLabels,
} from '../../../shared/InventoryAndContent/constants';
import { getAppGroupDisplayLabel, getBulkPlaceholder } from '../../../shared/InventoryAndContent/utils';
import { useBulkDrawerInitialValue } from '../hooks/useBulkDrawerInitialValue';
import { CustomBlockingRulesInputs } from './CustomBlockingRulesInputs';

type BulkDrawerProps = {
  contentOption: InventoryContentOption | AppInventoryOptionType;
  /**
   * Effective content option used for limit/helper text/placeholder calculations.
   * In App Inventory V2, `contentOption` always equals `APP_GROUP` (drives UI mode),
   * while the actual selected type (App Name vs App Bundle ID) is conveyed here.
   * Falls back to `contentOption` when not provided (legacy InventoryAndContent).
   */
  effectiveContentOption?: InventoryContentOption | AppInventoryOptionType;
  itemsCount: number;
  comparisonFieldCriteria?: Array<string>;
  comparisonType: ComparisonType;
  setContentOption: ((type: InventoryContentOption) => void) | ((type: AppInventoryOptionType) => void);
  setComparisonType: (type: ComparisonType) => void;
  onBulkAdd: (items: Array<string>) => void;
  onBulkClose: () => void;
  customValidation?: ((list: string[]) => Promise<ValidationResult>) | null;
  parentAccountFeatures: Record<string, boolean>;
  inventoryListItems?: string[];
  publisherInventoryRuleOptions: PublisherInventoryRuleOptions;
  setPublisherInventoryRuleOptions: Dispatch<SetStateAction<PublisherInventoryRuleOptions>>;
  targetingParams: InventoryAndContentState | AppInventoryState;
  displayContext?: DisplayContextsType;
  /**
   * Current App Inventory type (App Name or App Bundle ID).
   * Used to generate dynamic title for App Inventory Bulk Add.
   */
  appInventoryType?: AppInventoryType;
  /**
   * Backend-driven App Name validation with suggestions (powered by appGroupSearch).
   * Applied automatically when App Group / App Name is selected in Bulk Add.
   */
  appNameValidationV2?: (items: string[], existingItems?: string[]) => Promise<ValidationResultV2>;
  inventoryVersion?: AppInventoryVersionType;
  customComparisonOptions?: Record<string, ComparisonType[]>;
};

export const BulkDrawer = ({
  contentOption,
  effectiveContentOption,
  itemsCount,
  comparisonFieldCriteria = [],
  comparisonType,
  setContentOption,
  setComparisonType,
  onBulkAdd,
  onBulkClose,
  customValidation,
  parentAccountFeatures,
  inventoryListItems = [],
  publisherInventoryRuleOptions,
  setPublisherInventoryRuleOptions,
  targetingParams,
  displayContext,
  appInventoryType,
  appNameValidationV2,
  inventoryVersion,
  customComparisonOptions,
}: BulkDrawerProps): JSX.Element => {
  const { t } = useTranslation();
  const labels = useInventoryAndContentLabels();

  // App Inventory has its own simplified Bulk Add UI without Type/Comparison dropdowns
  const isAppInventory = contentOption === InventoryContentOption.APP_GROUP;

  // In App Inventory V2 `contentOption` always equals APP_GROUP (drives UI mode),
  // while the actual selected sub-type (App Name vs App Bundle ID) is carried by
  // `effectiveContentOption`. Use it for limit/helper text/placeholder lookups so that
  // e.g. Bundle ID respects `INVENTORY_LIMITS[APP_BUNDLE_ID]` (20000) instead of 500.
  const bulkContentOption = effectiveContentOption ?? contentOption;

  // V2: Check if we should use V2 validation with suggestions
  const isAppNameWithV2 = isAppInventory && appInventoryType === AppInventoryType.APP_NAME && !!appNameValidationV2;

  // Generate dynamic title for App Inventory Bulk Add (e.g., "Bulk Add - App Bundle ID")
  const appInventoryTitle =
    isAppInventory && appInventoryType
      ? `${t('Bulk Add')} - ${appInventoryType === AppInventoryType.APP_NAME ? t('App Name') : t('App Bundle ID')}`
      : undefined;

  const isPublisherInventoryRulesBundleIdSelected =
    contentOption === InventoryContentOption.PUBLISHER_INVENTORY_RULES &&
    publisherInventoryRuleOptions.blockingRuleOption === CustomBlockingRulesOptions.BUNDLE_ID;

  const bulkPlaceholder = isPublisherInventoryRulesBundleIdSelected
    ? getBulkPlaceholder(t, InventoryContentOption.APP_BUNDLE_ID, labels[InventoryContentOption.APP_BUNDLE_ID].label)
    : getBulkPlaceholder(t, bulkContentOption, labels[bulkContentOption].label);

  const validateBulk = useCallback(
    // biome-ignore lint/suspicious/useAwait: biome2 update
    async (list: Array<string>) => {
      const inventoryList = bulkContentOption === InventoryContentOption.APP_BUNDLE_ID ? inventoryListItems : [];
      const added: Set<string> = new Set(comparisonFieldCriteria);
      const inventoryListAdded = new Set(inventoryList);
      const duplicates: Array<string> = [];
      const validItems: Array<string> = [];

      list.forEach(item => {
        if (bulkContentOption === InventoryContentOption.PAGE_URL) {
          item = removeWhiteSpaces(item);
        }

        if (added.has(item) || inventoryListAdded.has(item)) {
          duplicates.push(item);
        } else {
          validItems.push(item);
          added.add(item);
        }
      });

      const errors: InvalidItemsError[] = [];

      if (duplicates.length) errors.push({ error: t('Duplicates.'), invalidItems: duplicates });

      return { errors, validItems };
    },
    [comparisonFieldCriteria, t, bulkContentOption, inventoryListItems]
  );

  // Backend-driven App Name validation (appGroupSearch) wrapped with current existing items.
  const validateWithV2 = useMemo(() => {
    if (!isAppNameWithV2 || !appNameValidationV2) return null;

    return (list: Array<string>) => {
      return appNameValidationV2(list, comparisonFieldCriteria);
    };
  }, [isAppNameWithV2, appNameValidationV2, comparisonFieldCriteria]);

  // Determine which validation to use
  const effectiveValidation = useMemo(() => {
    if (customValidation) return customValidation;
    if (validateWithV2) return validateWithV2;
    return validateBulk;
  }, [customValidation, validateWithV2, validateBulk]);

  const bulkUploadInitialValue = useBulkDrawerInitialValue({
    contentOption,
    displayContext,
    targetingParams,
  });

  const currentLimit = INVENTORY_LIMITS[bulkContentOption] - itemsCount;

  const shouldDisableActionButtons =
    contentOption === InventoryContentOption.PUBLISHER_INVENTORY_RULES
      ? !publisherInventoryRuleOptions?.isValid
      : false;

  const showComparsionDropdown =
    contentOption !== InventoryContentOption.PUBLISHER_ID &&
    contentOption !== InventoryContentOption.INSTANCE_HASH &&
    contentOption !== InventoryContentOption.ADUNIT &&
    contentOption !== InventoryContentOption.SITE &&
    contentOption !== InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID &&
    contentOption !== InventoryContentOption.KEYWORDS &&
    contentOption !== InventoryContentOption.PUBLISHER_INVENTORY_RULES &&
    contentOption !== InventoryContentOption.APP_GROUP;

  return (
    <BulkAddDrawer
      onApply={onBulkAdd}
      onClose={onBulkClose}
      placeholder={bulkPlaceholder}
      validate={effectiveValidation}
      helperText={bulkAddHelperPlaceholders(currentLimit, t)[bulkContentOption]}
      limit={currentLimit}
      childParams={contentOption}
      shouldDisableActionButtons={shouldDisableActionButtons}
      initialValue={bulkUploadInitialValue}
      title={appInventoryTitle}
      enableSuggestions={isAppNameWithV2}
      formatForDisplay={isAppNameWithV2 ? getAppGroupDisplayLabel : undefined}
    >
      {!isAppInventory && (
        <Grid
          container
          spacing={1}
          sx={{
            alignItems: 'flex-end',
          }}
        >
          <Grid size={6}>
            <InventoryContentOptionDropdown
              value={contentOption}
              onChange={setContentOption}
              isBulk={true}
              parentAccountFeatures={parentAccountFeatures}
              inventoryVersion={inventoryVersion}
            />
          </Grid>

          {contentOption === InventoryContentOption.PUBLISHER_INVENTORY_RULES && (
            <CustomBlockingRulesInputs
              publisherInventoryRuleOptions={publisherInventoryRuleOptions}
              setPublisherInventoryRuleOptions={setPublisherInventoryRuleOptions}
              targetingParams={targetingParams}
            />
          )}

          {showComparsionDropdown && (
            <Grid size={6}>
              <InventoryComparisonTypeDropdown
                option={contentOption}
                value={comparisonType}
                onChange={setComparisonType}
                customComparisonOptions={customComparisonOptions}
              />
            </Grid>
          )}
        </Grid>
      )}
    </BulkAddDrawer>
  );
};
